/* eslint-disable max-len */
import { CustomAttributeData } from '../../customAttributes/customAttributes';
import { CustomAttributeType } from '../../customAttributes/customAttributeType';
import { CustomAttributeValues } from '../../customAttributes/customAttributeValue';
import { SLTXFormatRootFileGroup } from '../../fileFormats/sltx/sltx';
import { SLTXFile } from '../../fileFormats/sltx/sltxFile';
import { SLTXTranslations } from '../../fileFormats/sltx/sltxTranslations';
import { TreeOptionSelections } from '../data/optionsSelection';
import { ConditionEvaluator } from './rulesConditionEvaluator';

export interface VarValues {
  attributeName: string;
  attributeValue: string
}

export class RulesEvaluationHelpers {

  // ---------------------------------------------------------------------------------------
  // Specific helpers
  // ---------------------------------------------------------------------------------------
  public static getSelectedOptionForComponent(
    sltfF: SLTXFile,
    component: SLTXFormatRootFileGroup,
    selectedOptions: TreeOptionSelections,
  ) : string {
    const selectedComponent = selectedOptions.find((opt) => opt.groupID === component.id);

    if (sltfF && selectedComponent) return SLTXTranslations.getSelectedOptionTranslation(component.id, selectedComponent.optionID, sltfF, false, 'en');
    return '<undefined-01>';
  }

  public static getVarValuesFromComposition(
    sltfF: SLTXFile | undefined,
    selectedOptions: TreeOptionSelections | undefined,
  ) : VarValues[] {
    const varValues : VarValues[] = [];

    if (!sltfF || !selectedOptions) return varValues;

    // Add a special one, with the model name
    varValues.push({ attributeName: 'item.name', attributeValue: `${sltfF.getRootFileContents().name}` });

    const optionGrooupsMap = sltfF.getRootFileContents()?.groups?.map((opt) => ({
      attributeName: `${decodeURI(opt.name)}`,
      attributeValue: `${this.getSelectedOptionForComponent(sltfF, opt, selectedOptions)}`,
    })) || [];
    varValues.push(...optionGrooupsMap);

    return varValues;
  }

  public static checkAttrCondition(
    condition: string | undefined,
    varValues: VarValues[] | undefined,
    attrValues: CustomAttributeValues | undefined,
  ): boolean {

    if (!condition || !condition.trim()) return true;
    const fullVarValues = [...varValues || []];
    // Backward-compatible operator due to the
    // old style !== and ===  use in attrs conditions
    const normalizedCondition = condition
      .replace(/!==/g, '!=')
      .replace(/===/g, '==');

    if (attrValues === undefined) return false;

    const custAttrsMap = attrValues.map((att) => ({
      attributeName: `${att.attributeKey}`,
      attributeValue: `${att.attributeValue}`,
    }));
    fullVarValues.push(...custAttrsMap);
    const bResult = ConditionEvaluator.evaluateAttrCondition(normalizedCondition, fullVarValues);

    return bResult;
  }

  public static evalConditions(
    varValues: VarValues[] | undefined,
    customAttributesData: CustomAttributeData[],
    customAttributesValues : CustomAttributeValues,
  ) : CustomAttributeValues | undefined {
    let bNeedsUpdate = false;
    const newAttrValues : CustomAttributeValues = [];
    customAttributesValues.forEach((aV) => {
      const attrData = customAttributesData.find((aD) => aD.key === aV.attributeKey);
      const newValue = aV;
      if (attrData && attrData.condition) {

        newValue.attributeEnabled = RulesEvaluationHelpers.checkAttrCondition(
          attrData.condition,
          varValues,
          customAttributesValues,
        );

        bNeedsUpdate ||= aV.attributeEnabled !== newValue.attributeEnabled;
        if (!newValue.attributeEnabled) {
          switch (attrData.type) {
            case CustomAttributeType.Text:
              newValue.attributeValue = '';
              break;
            case CustomAttributeType.Boolean:
              newValue.attributeValue = 'false';
              break;
            case CustomAttributeType.Decimal:
              newValue.attributeValue = '0.0';
              break;
            case CustomAttributeType.Integer:
              newValue.attributeValue = '0';
              break;
            case CustomAttributeType.Option:
              newValue.attributeValue = '';
              break;
            default:
          }
          newAttrValues.push(newValue);
        }
      }
    });
    return bNeedsUpdate ? newAttrValues : undefined;
  }

  public static checkAttrCondition2(
    sltfF: SLTXFile,
    condition: string | undefined,
    selectedOptions: TreeOptionSelections,
    attrValues: CustomAttributeValues | undefined,
  ): boolean {
    if (!condition || !condition.trim()) return true;

    // Backward-compatible operator due to the
    // old style !== and ===  use in attrs conditions
    const normalizedCondition = condition
      .replace(/!==/g, '!=')
      .replace(/===/g, '==');

    if (attrValues === undefined) return false;

    const varValues: VarValues[] = [];

    varValues.push({ attributeName: 'item.name', attributeValue: `${sltfF.getRootFileContents().name}` });

    const custAttrsMap = attrValues.map((att) => ({
      attributeName: `${att.attributeKey}`,
      attributeValue: `${att.attributeValue}`,
    }));
    varValues.push(...custAttrsMap);

    const optionGrooupsMap = sltfF.getRootFileContents()?.groups?.map((opt) => ({
      attributeName: `${decodeURI(opt.name)}`,
      attributeValue: `${this.getSelectedOptionForComponent(sltfF, opt, selectedOptions)}`,
    })) || [];
    varValues.push(...optionGrooupsMap);

    const bResult = ConditionEvaluator.evaluateAttrCondition(normalizedCondition, varValues);

    return bResult;
  }

  public static evalConditions2(
    sltxFile: SLTXFile,
    customAttributesValues : CustomAttributeValues,
    selectedOptions: TreeOptionSelections,
  ) : CustomAttributeValues | undefined {
    let bNeedsUpdate = false;
    const newAttrValues : CustomAttributeValues = [];
    customAttributesValues.forEach((aV) => {
      const attrData = sltxFile.getRootFileContents().customAttributes?.find((aD) => aD.key === aV.attributeKey);
      const newValue = aV;
      if (attrData && attrData.condition) {

        newValue.attributeEnabled = RulesEvaluationHelpers.checkAttrCondition2(
          sltxFile,
          attrData.condition,
          selectedOptions,
          customAttributesValues,
        );

        bNeedsUpdate ||= aV.attributeEnabled !== newValue.attributeEnabled;
        if (!newValue.attributeEnabled) {
          switch (attrData.type) {
            case CustomAttributeType.Text:
              newValue.attributeValue = '';
              break;
            case CustomAttributeType.Boolean:
              newValue.attributeValue = 'false';
              break;
            case CustomAttributeType.Decimal:
              newValue.attributeValue = '0.0';
              break;
            case CustomAttributeType.Integer:
              newValue.attributeValue = '0';
              break;
            case CustomAttributeType.Option:
              newValue.attributeValue = '';
              break;
            default:
          }
          newAttrValues.push(newValue);
        }
      }
    });
    return bNeedsUpdate ? newAttrValues : undefined;
  }
}
